import { useQuery } from '@tanstack/react-query';
import { pollingQueryOptions } from '@/lib/query-polling';
import {
  contarLeiturasInconsistentes,
  tiltimetroResumo,
  ultimaLeituraAlterada,
  type TiltResumo,
} from '@/domain/dashboard';
import { type TarpRecordStatus } from '@/domain/tarp/status';
import { isBusinessHours } from '@/domain/cobertura';
import { unitForType } from '@/domain/instrumentos/instrument-type';
import { useDams } from '@/hooks/useDams';
import { useDataSources, type DamOption, type TarpRecord } from '@/services/datasources';
import {
  FORA_DE_SERVICO, leituraGovernante, nivelExibidoDeInstrumento, SEM_FAIXA, SEM_LEITURA,
  SEM_SINAL, type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import {
  computeBadInstruments, itensQuePedemAcao, type InstrumentWithLevel, type ItemAtencao,
} from '@/domain/dashboard/atencao';
import type { Instrument, Reading, TarpLevel } from '@/domain';

// ── Funções de derivação pura (exportadas para testes) ──────────

/**
 * Conta instrumentos por nível usando a leitura mais recente de cada um.
 *
 * GUPM-228 — instrumento sem leitura tem balde próprio (`SEM_LEITURA`) e deixa
 * de inflar o verde. O card "Instrumentos normais" contava o `EM-01`, que não
 * tem uma única leitura, como normal.
 *
 * `nivelOverall` e o `SemaphoreCard` continuam derivando de `LEVEL_ORDER` (os 4
 * níveis reais), então falta de leitura não escala o semáforo.
 */
export function computeByLevel(
  instruments: Instrument[],
  readings: Reading[],
): Record<NivelExibido, number> {
  const byLevel: Record<NivelExibido, number> = {
    NORMAL: 0, ATENCAO: 0, ALERTA: 0, EMERGENCIA: 0,
    // GUPM-303 — balde proprio. Sem ele o tiltimetro mudo cairia num dos 4 niveis e o
    // denominador do card afirmaria medicao que nao houve.
    [SEM_SINAL]: 0,
    // GUPM-307 — balde proprio, fora dos 4 niveis reais. E o que faz o `nivelOverall`
    // (`LEVEL_ORDER.find`) parar de ser EMERGENCIA por causa de uma leitura congelada, e
    // com ele o botao de emergencia do header para de pulsar em toda tela.
    [FORA_DE_SERVICO]: 0,
    [SEM_FAIXA]: 0, [SEM_LEITURA]: 0,
  };
  // GUPM-270 — RETIRADO fora do contador. Os 26 inativos (todos ausentes do
  // inventário v2: o fornecedor parou de listá-los) inflavam os dois números do card:
  // o denominador ("53/84", onde o honesto é 53/58) e o balde "sem limite definido",
  // em que 26 dos 30 eram retirados — que nunca terão carta, porque saíram do
  // monitoramento. O filtro vive DENTRO da função, não no chamador, para o próximo
  // consumidor não precisar lembrar.
  for (const inst of instruments.filter((i) => i.isActive)) {
    // GUPM-270 — a regra do pior canal com faixa vale AQUI TAMBÉM, e este é o ponto
    // mais grave de todos: `byLevel` alimenta `nivelOverall`, que vai ao `alertStore` e
    // pinta o indicador de severidade do Header, visível em toda tela. Com a forma
    // antiga (`filter → sort(ts) → [0]`) um tiltímetro com eixo em ALERTA podia ser
    // contado como NORMAL — a linha escolhida entre canais empatados no `ts` saía por
    // ordem de array, e a da temperatura carrega o `NORMAL` default da coluna.
    byLevel[nivelExibidoDeInstrumento(inst, readings)]++;
  }
  return byLevel;
}

/**
 * Payload do stat card "Última leitura" — GUPM-247.
 *
 * Exportado pelo mesmo motivo que `computeByLevel`: é o cálculo que erra, e
 * testá-lo pelo hook exigiria montar o feed inteiro por mock.
 *
 * GUPM-228 — sem leitura atual o card sai neutro em vez de verde. Caso de borda
 * (a alterada vem do feed, então normalmente há atual), mas verde por omissão é
 * a mentira que aquele card removeu do resto do app.
 * GUPM-247 — e o nível considera a existência de FAIXA. Sem isso o card ficava
 * verde para os 14 tiltímetros (todos sem faixa, e são o que mais lê): o mesmo
 * instrumento apareceria "Normal" aqui e "Sem faixa" no Resumo Técnico.
 */
export function montarUltimaLeitura(
  alterada: { instrumentId: string; value: number } | null | undefined,
  instrument: Instrument | null | undefined,
  current: Reading[],
  // Obrigatório de propósito (GUPM-325): com default `[]` um chamador que esquecesse
  // de passar teria `damCode: null` calado, e o card voltaria a estampar o código sem
  // estrutura sem nada quebrar. Assim o `tsc` cobra em cada ponto de chamada.
  dams: { id: string; code: string }[],
): UltimaLeitura | null {
  if (!alterada || !instrument) return null;
  // C-89 — com faixa POR CANAL, valor e cor saem da MESMA leitura: a que governa o
  // instrumento (pior canal com faixa, GUPM-270). `alterada` só escolhe QUAL
  // instrumento o card mostra; o canal dela vem de um desempate por ordem do array
  // quando os eixos têm o mesmo `ts`, e em 16/10/2026 o card estampou 1,7° (eixo A,
  // normal) com a cor de Emergência do eixo B. Sem faixa por canal o valor continua o
  // que mudou (comportamento anterior): ali a cor não vem do pior canal com faixa, então
  // não há o par valor × cor a alinhar por esta regra. Sem leitura atual em canal com
  // faixa (o feed e a `readings_current` não são consultados juntos), fica o que mudou.
  const canais = instrument.canaisComFaixa ?? [];
  const governante = canais.length > 0
    ? leituraGovernante(current.filter((r) => r.instrumentId === instrument.id), canais)
    : undefined;
  return {
    code: instrument.code,
    // GUPM-325 — a barragem QUALIFICA o código: `TIL-01` e `TIL-02` existem em A, B
    // e C (produção), e o card estampava o código sozinho. `null` quando a barragem
    // não está na lista (instrumento só-API, filtro de escopo) — quem renderiza cai
    // no código puro, nunca num `-TIL-02` com separador pendurado.
    damCode: dams.find((d) => d.id === instrument.damId)?.code ?? null,
    value: governante?.value ?? alterada.value,
    unit: unitForType(instrument.type),
    // GUPM-270 — a COR sai da mesma regra que o resto do app. Era um `find` pelo
    // instrumentId sem filtro de canal: a primeira linha que casasse decidia, e para o
    // tiltímetro isso podia ser a temperatura — o card estampava 8,1° em VERDE.
    level: nivelExibidoDeInstrumento(instrument, current),
  };
}

/**
 * Forma do payload do stat card "Última leitura" (D6).
 *
 * Tipo NOMEADO desde o GUPM-325 — era um literal inline repetido no retorno de
 * `montarUltimaLeitura`, na interface `M1Data` e nas props do componente. Com o
 * nome, acrescentar um campo (foi o caso do `damCode`) quebra a compilação de quem
 * o monta e de quem o consome, que é onde tem de quebrar.
 */
export interface UltimaLeitura {
  code: string;
  /** Código da barragem (`A`/`B`/`C`); null quando não resolvida. */
  damCode: string | null;
  value: number;
  unit: string;
  level: NivelExibido;
}

// C-24 — a derivação do "Precisa da sua atenção" vive no domínio desde o sino do header,
// que precisa da MESMA lista. Re-exportada aqui para os testes e consumidores que já a
// importavam deste módulo.
export {
  computeBadInstruments, computeSemSinal,
  type InstrumentWithLevel, type ItemAtencao, type ItemSemSinal,
} from '@/domain/dashboard/atencao';

// `isBusinessHours` vive no domínio (`@/domain/cobertura`) — o widget de cobertura
// (TechCoverageCard) importa de lá. Re-exportado aqui só para compat com o teste
// `useM1Data.test.ts`, que valida a regra importando do hook (histórico GUPM-119).
export { isBusinessHours };

// ── Hook ────────────────────────────────────────────────────────

/** Resumo das TARPs para o card do dashboard (GUPM-191 D3, fiel ao `buildTarpSummary` do V8). */
export interface TarpsResumo {
  // Contagem por status com TODOS os valores do enum (inclui ENCERRADA e o rascunho
  // EM_ABERTURA). Quem agrupa nos 3 estágios do card é `agruparPorEstagio`; aqui não se
  // escolhe status, senão uma TARP cai fora de todo contador (GUPM-325). Partial: um
  // status sem TARPs não aparece no mapa (o estágio soma 0).
  //
  // DIVERGÊNCIA DELIBERADA (GUPM-325, decidida pelo dono): a pill "Em abertura" conta
  // rascunhos que `ativas` NÃO exibe. É o inverso do achado #1 do GUPM-191, que tirou o
  // badge justamente por contar sem exibir — e a inversão foi confirmada: o CONTADOR
  // precisa somar tudo (não contar escondia TARP de barragem, inaceitável num app de
  // segurança), e a LISTA é, de propósito, só das que aguardam o RT — quem precisa de
  // ação. Rascunho se acompanha no M3. Não "consertar" um lado achando que é bug.
  porStatus: Partial<Record<TarpRecordStatus, number>>;
  /** Total de TARPs ativas (não-encerradas, não-rascunho); `ativas` é o slice(0,4). */
  ativasTotal: number;
  ativas: TarpRecord[];
}

export interface M1Data {
  instruments: Instrument[];
  /** Barragens ativas (A/B/C) — agrupam o painel de Thresholds por Estrutura (GUPM-206). */
  dams: DamOption[];
  readings: Reading[];
  byLevel: Record<NivelExibido, number>;
  /** Todos os instrumentos com leitura atual não-normal (tabela técnica, deep-link). */
  badInstruments: InstrumentWithLevel[];
  /**
   * Subconjunto de `badInstruments` que ainda PEDE AÇÃO — alimenta o bloco
   * "Precisa da sua atenção". Instrumento com TARP em curso sai (GUPM-221);
   * escalada de nível e prazo de baixa vencido nunca são suprimidos.
   */
  anomaliasNotificaveis: ItemAtencao[];
  nivelOverall: TarpLevel;
  sireneFalhas: number;
  /** Instrumentos com leitura NORMAL / total (GUPM-191 D4). */
  instrumentosNormais: { normais: number; total: number };
  /** Resumo dos tiltímetros (GUPM-191 D5). */
  tiltResumo: TiltResumo;
  /** Leituras inconsistentes (não-normais) nas últimas 24h (GUPM-191 D7). */
  eventosUlt24h: number;
  /** Última leitura efetivamente ALTERADA (GUPM-191 D6); `level` colore o card (fiel ao V8). */
  lastReading: UltimaLeitura | null;
  /** Timestamp da leitura mais recente ingerida (para o timer do cron, D8). */
  lastUpdateTs: Date | null;
  tarpsResumo: TarpsResumo;
  isLoading: boolean;
  error: Error | null;
}

export function useM1Data(): M1Data {
  const { instrumentacao, sirenes, tarp } = useDataSources();

  const { data: instruments = [], isLoading: loadingInst, error: errInst } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentacao.listInstruments(),
    ...pollingQueryOptions,
  });

  // GUPM-136 — última leitura POR instrumento para nível/semáforo/tabela. Sem isso,
  // o top-N global (dominado pelo TILT de 30min) escondia ALERTA de instrumento lento.
  const { data: current = [], isLoading: loadingRead, error: errRead } = useQuery({
    queryKey: ['readings', 'current'],
    queryFn: () => instrumentacao.listCurrentReadings(),
    ...pollingQueryOptions,
  });

  // Feed de eventos das últimas 24h — mantém o top-N global (standby, não deletar).
  // GUPM-234: `error` propagado. Sem isso, uma falha de leitura (RLS/rede) deixava a
  // tela "carregada" mostrando 0 eventos em 24h, "Última leitura: —" e o timer do
  // cron parado — o plantonista lê "nada aconteceu".
  const { data: feed = [], error: errFeed } = useQuery({
    queryKey: ['readings', 'latest'],
    queryFn: () => instrumentacao.listLatestReadings({ limit: 100 }),
    ...pollingQueryOptions,
  });

  // Idem para sirenes: hoje o datasource é mock in-memory (nunca falha), mas quando
  // virar a integração TCal um erro engolido viraria "0 sirenes em falha".
  const { data: sireneList = [], error: errSirenes } = useQuery({
    queryKey: ['sirenes'],
    queryFn: () => sirenes.list(),
    ...pollingQueryOptions,
  });

  // Barragens ativas (A/B/C) — agrupam o painel de Thresholds por Estrutura (D-thr).
  // Reusa o hook `useDams` (fonte única da query) em vez de duplicar queryKey/queryFn.
  const { data: dams = [], error: errDams } = useDams();

  // Resumo das TARPs (D3) — traz TODOS os status (o card agrupa em 3 estágios), sem
  // realtime (só polling; o push ao vivo é do M3). Propaga o erro: cair em
  // [] silencioso mostraria "nenhuma TARP" mesmo com a query falhando — enganoso num
  // app de segurança de barragem (achado #2 do review).
  //
  // FURO CONHECIDO (GUPM-325): `listTarps` é `opened_at desc` + limit(200), sem janela.
  // (a) "Baixadas" acumula desde sempre ao lado de dois contadores do "agora";
  // (b) ao passar de 200 TARPs o corte é SILENCIOSO e derruba as mais antigas por
  // `opened_at` — "Baixadas" começaria a encolher, e uma TARP aberta há muito e ainda
  // ativa sairia do contador. É o mesmo risco que o comentário de `listBaixasEncerradas`
  // já documenta e resolve ordenando por `closed_at`.
  //
  // Horizonte: LONGO. Abertura de TARP é sempre MANUAL — a automação (perda de sinal,
  // limiar) só publica aviso em "Precisa da sua atenção"; ninguém abre TARP por evento.
  // Então o teto de 200 é limitado por ação humana, não por volume de telemetria: hoje
  // são 10 TARPs no total. Não é urgente; é dívida a pagar antes de anos de operação.
  const { data: tarpsAll = [], error: errTarps } = useQuery({
    queryKey: ['tarps', 'dashboard'],
    queryFn: () => tarp.listTarps(),
    ...pollingQueryOptions,
  });

  const byLevel = computeByLevel(instruments, current);
  const badInstruments = computeBadInstruments(instruments, current, dams);
  // nivelOverall derivado de byLevel (leitura mais recente por instrumento),
  // evitando divergência com readings históricas no top-100.
  const LEVEL_ORDER: TarpLevel[] = ['EMERGENCIA', 'ALERTA', 'ATENCAO', 'NORMAL'];
  const nivelOverall: TarpLevel = LEVEL_ORDER.find((l) => byLevel[l] > 0) ?? 'NORMAL';

  // D7: eventos = leituras inconsistentes (não-normais) das últimas 24h.
  const eventosUlt24h = contarLeiturasInconsistentes(feed, new Date());

  // D6: última leitura efetivamente ALTERADA (repetição não conta como nova).
  //
  // GUPM-270 — só canal COM FAIXA entra. O card estampa o número com a unidade do
  // TIPO do instrumento, então a temperatura de um tiltímetro apareceria como
  // "11,2 °" — grau de inclinação — quando são 11,2 °C. Canal sem régua não é a
  // grandeza que este card se propõe a mostrar.
  const canaisPorInstrumento = new Map(
    instruments.map((i) => [String(i.id), i.canaisComFaixa ?? []] as const),
  );
  const feedComFaixa = feed.filter((r) => {
    const canais = canaisPorInstrumento.get(String(r.instrumentId)) ?? [];
    return canais.length === 0 ? true : r.canal !== null && canais.includes(r.canal);
  });
  const alterada = ultimaLeituraAlterada(feedComFaixa);
  const lastReadingInst = alterada
    ? instruments.find((i) => i.id === alterada.instrumentId)
    : null;
  // Nível do instrumento da última leitura alterada — colore o card (V8: levelColor).
  // Vem da leitura ATUAL do mesmo instrumento (a alterada carrega só valor/ts).
  const lastReading = montarUltimaLeitura(alterada, lastReadingInst, current, dams);

  // D8: timestamp da leitura mais recente ingerida (mesmo que repetida) — "última atualização".
  const lastUpdateTs =
    feed.length > 0 ? feed.reduce((a, b) => (a.ts > b.ts ? a : b)).ts : null;

  // D4/D5/D3.
  // GUPM-270 — o total sai da SOMA dos baldes, não de `instruments.length`: assim o
  // denominador é exatamente a população que foi classificada (só os ativos) e não tem
  // como divergir do contador ao lado dele.
  const instrumentosNormais = {
    normais: byLevel.NORMAL,
    total: Object.values(byLevel).reduce((a, b) => a + b, 0),
  };
  const tiltResumo = tiltimetroResumo(instruments, current);
  const porStatus = tarpsAll.reduce<Partial<Record<TarpRecordStatus, number>>>((acc, t) => {
    acc[t.status] = (acc[t.status] ?? 0) + 1;
    return acc;
  }, {});
  // Lista "ativas" do V8 = não-encerradas; excluímos também o rascunho EM_ABERTURA
  // (conceito do app, sem par no V8 — não é uma TARP "de verdade" ainda).
  const ativasAll = tarpsAll.filter(
    (t) => t.status !== 'EM_ABERTURA' && t.status !== 'ENCERRADA',
  );
  const tarpsResumo: TarpsResumo = {
    porStatus,
    ativasTotal: ativasAll.length,
    ativas: ativasAll.slice(0, 4),
  };

  // GUPM-221 — o bloco "Precisa da sua atenção" pede AÇÃO; instrumento já coberto
  // por TARP em curso sai. `badInstruments` segue completo: o Resumo Técnico e o
  // deep-link do "→ Fluxo" precisam enxergar o instrumento mesmo suprimido.
  // C-24 — mesma função que alimenta o sino do header (`itensQuePedemAcao`).
  const anomaliasNotificaveis = itensQuePedemAcao(instruments, current, dams, tarpsAll, new Date());

  return {
    instruments,
    dams,
    readings: current, // tabela do M1 usa a leitura atual por instrumento (não o feed)
    byLevel,
    badInstruments,
    anomaliasNotificaveis,
    nivelOverall,
    sireneFalhas: sireneList.filter((s) => !s.isOnline).length,
    instrumentosNormais,
    tiltResumo,
    eventosUlt24h,
    lastReading,
    lastUpdateTs,
    tarpsResumo,
    isLoading: loadingInst || loadingRead,
    // Propaga o erro de `dams` também: cair em [] silencioso esconderia o painel de
    // Thresholds mesmo com instrumentos carregados (mesmo raciocínio de errTarps).
    // `errFeed`/`errSirenes` entraram no GUPM-234 — eram as duas que faltavam.
    error: (errInst ?? errRead ?? errTarps ?? errDams ?? errFeed ?? errSirenes) as Error | null,
  };
}
